import { useRef, type KeyboardEvent } from 'react';
import { SCAN_IMAGE_TABS, nextScanImageTab, type ScanImageTabId } from './scanImageTabs';

const TAB_LOOK = {
    list: 'bg-gray-100',
    active: 'bg-gray-900 text-white shadow-sm',
    idle: 'text-gray-600 hover:text-gray-900',
    activeCount: 'text-white/60',
    idleCount: 'text-gray-400'
} as const;

interface ScanImageTabListProps {
    baseId: string;
    activeTab: ScanImageTabId;
    counts: Readonly<Record<ScanImageTabId, number | null>>;
    onChange: (tab: ScanImageTabId) => void;
}

export function ScanImageTabList({ baseId, activeTab, counts, onChange }: ScanImageTabListProps) {
    const tabsRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = nextScanImageTab(activeTab, event.key === 'ArrowRight' ? 1 : -1);
        onChange(next);
        tabsRef.current?.querySelector<HTMLButtonElement>(`#${baseId}-tab-${next}`)?.focus();
    };

    return (
        <div
            ref={tabsRef}
            role="tablist"
            aria-label="Scan images"
            onKeyDown={handleKeyDown}
            className={`inline-flex min-w-0 rounded-lg p-[3px] ${TAB_LOOK.list}`}
        >
            {SCAN_IMAGE_TABS.map((tab) => {
                const isActive = tab.id === activeTab;
                const count = counts[tab.id];
                return (
                    <button
                        key={tab.id}
                        id={`${baseId}-tab-${tab.id}`}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={`${baseId}-panel`}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(tab.id)}
                        className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${
                            isActive ? TAB_LOOK.active : TAB_LOOK.idle
                        }`}
                    >
                        {tab.label}
                        {count !== null && (
                            <span
                                className={`font-medium tabular-nums ${isActive ? TAB_LOOK.activeCount : TAB_LOOK.idleCount}`}
                            >
                                {count.toLocaleString('en-US')}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
